import React from 'react';
import { Controller, Control, Path, FieldValues } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import useAutocompleteControlledInput from '../hooks/useAutocompleteControlledInput';
import { AutocompleteResult } from '../infrastructure/autocompleteResultRepository.interface';

interface AutocompleteControlledInputProps<TField extends FieldValues> {
  control: Control<TField>;
  name: Path<TField>;
  key: string;
  placeholder?: string;
}

export const AutocompleteControlledInput = <TField extends FieldValues>(
  props: AutocompleteControlledInputProps<TField>
) => {
  const { control, name } = props;
  const endpoint = String(props.name).includes('medication')
    ? 'medications'
    : 'problems';

  const { options, setSearchedValue, autocompleteOptions, setOpen, loading } =
    useAutocompleteControlledInput(endpoint);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: true
      }}
      render={({ field, fieldState: { error } }) => {
        const { onChange, value, ref } = field;
        return (
          <>
            <Autocomplete
              value={
                value != null && value !== undefined
                  ? options.find((option) => {
                      return value === option.key;
                    }) ?? null
                  : null
              }
              blurOnSelect={false}
              clearOnBlur={false}
              getOptionLabel={(option) => {
                return option.description;
              }}
              onOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              onChange={(event: any, newValue: AutocompleteResult | null) => {
                onChange(newValue ?? null);
              }}
              options={autocompleteOptions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={props.placeholder}
                  inputRef={ref}
                  onChange={(ev) => {
                    setSearchedValue(ev.target.value);
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    )
                  }}
                  sx={{
                    '& .MuiInputBase-root.MuiOutlinedInput-root': {
                      borderRadius: '1.75rem'
                    }
                  }}
                />
              )}
            />
            {error != null ? (
              <span style={{ color: 'red' }}>{error.message}</span>
            ) : null}
          </>
        );
      }}
    />
  );
};
